import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { seedJobs, useJobs } from '@/api/jobs';
import { dashboardQuery, organizationsListQuery, websitesListQuery } from '@/api/queries';
import type { Dashboard, Job, JobSummary } from '@/api/types';
import { paths, type SiteTab } from '@/app/paths';
import { useSession } from '@/app/session';
import { Card, CardHead, IconTile, Kpi, Notch, Pill, Segmented, type IconName } from '@/design-system';
import { now } from '@/lib/clock';
import { alertMeta, alertPath, alertTitle } from '@/features/reports/alerts';
import { demoToProduction, funnelQuery, publishingQuery } from '@/features/reports/api';
import { domainsQuery as domainSettingsQuery } from '@/features/settings/api';
import { formatNumber, formatUsd } from '@/lib/format';
import { MyTasksCard } from './MyTasks';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { LoadError } from '@/shell/LoadError';
import s from './dashboard.module.css';

type Period = 'weeks' | 'months' | 'years';
type Tv = (k: string, o?: Record<string, unknown>) => string;

/** Tashkent's calendar parts of a moment. */
function local(d: Date) {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Tashkent', weekday: 'short', day: 'numeric', month: 'numeric', hour: 'numeric', hourCycle: 'h23' }).formatToParts(d);
  const get = (k: string) => p.find((x) => x.type === k)?.value ?? '';
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { weekday: wd, day: Number(get('day')), month: Number(get('month')) - 1, hour: Number(get('hour')) };
}

function greetingKey(hour: number): 'morning' | 'day' | 'evening' | 'night' {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'day';
  if (hour >= 18 && hour < 23) return 'evening';
  return 'night';
}

/** A chart label from the API: «09.09» for a week (ISO date), «Май» for a month, the year as is. */
function barLabel(label: string, monthsShort: string[]): string {
  const day = /^\d{4}-(\d{2})-(\d{2})$/.exec(label);
  if (day) return `${day[2]}.${day[1]}`;
  const month = /^\d{4}-(\d{2})$/.exec(label);
  if (month) return monthsShort[Number(month[1]) - 1] ?? label;
  return label;
}

const JOB_ICON: Record<string, IconName> = {
  'research.run': 'scan-search',
  'ai.generate_variants': 'sparkles',
  'domain.verify': 'shield-check',
  'store.catalog_import': 'package',
  'store.provision': 'shopping-bag',
  'store.theme_sync': 'palette',
  'publish.deploy': 'rocket',
  'publish.rollback': 'undo-2',
};

/** The site tab a website job opens (the rest open Обзор). */
const JOB_TAB: Record<string, SiteTab> = {
  'ai.generate_variants': 'variants',
  'publish.deploy': 'deploy',
  'publish.rollback': 'deploy',
  'store.provision': 'sync',
  'store.theme_sync': 'sync',
  'store.catalog_import': 'sync',
};

/** / — the dashboard (DESIGN_SPEC §4.1). Fields the API sends as null show the design's empty states. */
export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { t: ts } = useTranslation('shell');
  useBreadcrumbs([{ label: ts('nav.dashboard') }]);
  const { me } = useSession();
  const [period, setPeriod] = useState<Period>('weeks');
  const q = useQuery({ ...dashboardQuery(period), placeholderData: keepPreviousData });
  const today = local(now());
  const tc = useTranslation('common').t as unknown as (k: string, o?: Record<string, unknown>) => string[];
  const weekdays = tc('weekdays', { returnObjects: true });
  const monthsGen = tc('months_genitive', { returnObjects: true });
  const firstName = me.full_name.trim().split(/\s+/)[0] ?? me.full_name;
  const weekday = weekdays[today.weekday] ?? '';

  return (
    <>
      <div className={s.top}>
        <div className={s.titles}>
          <span className={s.eyebrow}>{t('eyebrow', { weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1), day: today.day, month: monthsGen[today.month] })}</span>
          <h1 className={s.h1}>{t(`greeting.${greetingKey(today.hour)}`, { name: firstName })}</h1>
        </div>
        <Segmented label={t('periods_label')} value={period} onChange={setPeriod} options={(['weeks', 'months', 'years'] as const).map((k) => ({ value: k, label: t(`periods.${k}`) }))} />
      </div>
      {q.isError ? <LoadError error={q.error} onRetry={() => void q.refetch()} /> : q.data && <Board d={q.data} period={period} />}
    </>
  );
}

function Board({ d, period }: { d: Dashboard; period: Period }) {
  const { t } = useTranslation('dashboard');
  const tv = t as unknown as Tv;
  const tc = useTranslation('common').t as unknown as (k: string, o?: Record<string, unknown>) => string[];
  const dash = useTranslation('common').t('dash');
  const navigate = useNavigate();
  const patternId = useId();
  const month = local(now()).month;
  const months = tc('months', { returnObjects: true });
  const monthsPrep = tc('months_prepositional', { returnObjects: true });
  const monthsShort = tc('months_short', { returnObjects: true });
  const failing = d.alerts.find((a) => a.kind === 'build_failed')?.target as { website?: string; variant?: string } | undefined;

  // The jobs: the dashboard's list, kept live by the event stream (the store holds the newer state).
  useEffect(() => seedJobs(d.running_jobs as unknown as Job[]), [d.running_jobs]);
  const store = useJobs();
  const base = d.running_jobs as unknown as JobSummary[];
  const byId = new Map(store.map((j) => [j.id, j]));
  const jobs = [...base.map((j) => ({ ...j, ...byId.get(j.id) })), ...store.filter((j) => !base.some((b) => b.id === j.id) && (j.status === 'running' || j.status === 'queued'))];
  const orgs = useQuery(organizationsListQuery).data ?? [];
  const sites = useQuery(websitesListQuery).data ?? [];
  // «AvtoDetal Pro · вариант C»: the newest build failure (P5-E05 `build_failed` alert: website, variant).
  const failedSite = failing?.website ? sites.find((x) => x.id === failing.website) : undefined;
  const errorsLabel = failedSite
    ? failing?.variant
      ? t('kpi.errors_variant', { brand: failedSite.organization.brand_name, key: failing.variant })
      : failedSite.name
    : (d.alerts.find((a) => a.kind === 'build_failed')?.subtitle ?? dash);
  // The demo zone under «Активные демо»: the dashboard's (GAP), else Settings → Домены for an Admin.
  const { isAdmin } = useSession();
  const zoneSetting = useQuery({ ...domainSettingsQuery, enabled: isAdmin && !d.demo_zone }).data?.values.demo_zone;
  const demoZone = d.demo_zone ?? (zoneSetting ? (zoneSetting.startsWith('*.') ? zoneSetting : `*.${zoneSetting}`) : dash);

  const bars = d.publications ?? [];
  const totals = bars.map((b) => b.demo + b.production);
  const max = Math.max(1, ...totals);
  const gen = d.generations;
  const genPct = gen.used !== null && gen.limit ? Math.round((gen.used / gen.limit) * 100) : 0;

  const jobTarget = (j: JobSummary) => {
    if (j.label) return j.label;
    if (j.target.entity === 'website') return sites.find((x) => x.id === j.target.id)?.name ?? j.label ?? dash;
    if (j.type === 'research.run' || j.target.entity === 'organization') return orgs.find((o) => o.id === j.organization_id)?.brand_name ?? j.label ?? dash;
    return j.label ?? dash;
  };
  const jobStatus = (j: JobSummary) => {
    const pct = Math.round(j.progress * 100);
    if (j.status === 'succeeded') return t('jobs.done');
    if (j.status === 'failed' || j.status === 'dead') return t('jobs.failed');
    if (j.status === 'cancelled') return t('jobs.cancelled');
    if (j.status === 'queued') return t('jobs.queued');
    if (j.type === 'research.run' || j.type === 'ai.generate_variants') {
      return j.eta_seconds ? t('jobs.eta', { pct, minutes: Math.max(1, Math.round(j.eta_seconds / 60)) }) : t('jobs.pct', { pct });
    }
    return j.step ?? t('jobs.pct', { pct });
  };
  const openJob = (j: JobSummary) => {
    if (j.type === 'research.run' && j.organization_id) navigate(paths.organization(j.organization_id, 'research'));
    else if (j.target.entity === 'website' && j.target.id) navigate(paths.site(j.target.id, JOB_TAB[j.type] ?? 'overview'));
    else if (j.organization_id) navigate(paths.organization(j.organization_id));
  };
  const openAlert = (target: Record<string, unknown>, kind: string) => {
    const to = alertPath(kind, target);
    if (to) navigate(to);
  };

  return (
    <>
      <div className={s.grid240}>
        <Card padding={24} relative className={s.hero}>
          <div className={s.planet} aria-hidden>
            <div className={s.sphere} />
            <div className={s.ring} />
          </div>
          <Notch
            actions={[
              { icon: 'globe', label: t('hero.globe'), onClick: () => navigate(paths.sites()) },
              { icon: 'sparkles', label: t('hero.sparkles') },
            ]}
          />
          <div className={s.heroHead}>
            <span className={s.eyebrow12}>{t('hero.eyebrow')}</span>
            <span className={s.heroTitle}>{t('hero.title')}</span>
          </div>
          <div className={s.heroBody}>
            <div className={s.heroValueRow}>
              <span className={s.heroValue}>{d.production_stores.value ?? dash}</span>
              {d.organizations.value !== null && <span className={s.heroOf}>{t('hero.of', { count: d.organizations.value })}</span>}
            </div>
            <div className={s.heroNotes}>
              {d.production_stores.delta ? <span className={s.heroDelta}>{t('hero.delta', { count: d.production_stores.delta, month: months[month] })}</span> : null}
              <span>{d.active_domains === null ? t('hero.domains_unknown') : t('hero.domains', { count: d.active_domains })}</span>
            </div>
          </div>
        </Card>
        <Kpi
          eyebrow={t('kpi.orgs_eyebrow')}
          title={t('kpi.orgs')}
          value={d.organizations.value ?? dash}
          delta={d.organizations.delta ? `+${d.organizations.delta}` : undefined}
          sub={t('kpi.orgs_sub', { trial: d.organizations.trial, archived: d.organizations.archived })}
          notch={[{ icon: 'building-2', label: t('kpi.open'), onClick: () => navigate(paths.organizations()) }]}
          onClick={() => navigate(paths.organizations())}
        />
        <Kpi
          eyebrow={t('kpi.demos_eyebrow')}
          title={t('kpi.demos')}
          value={d.active_demos.value ?? dash}
          delta={d.active_demos.delta ? `+${d.active_demos.delta}` : undefined}
          sub={d.active_demos.value === null ? t('kpi.demos_empty') : t('kpi.demos_sub', { zone: demoZone })}
          notch={[{ icon: 'flask-conical', label: t('kpi.open'), onClick: () => navigate(paths.sites()) }]}
          onClick={() => navigate(paths.sites())}
        />
        <Kpi
          eyebrow={t('kpi.errors_eyebrow')}
          title={t('kpi.errors')}
          value={d.build_errors_24h.value ?? dash}
          delta={d.build_errors_24h.value ? t('kpi.errors_check') : undefined}
          deltaTone="err"
          sub={d.build_errors_24h.value === null ? t('kpi.errors_empty') : d.build_errors_24h.value ? errorsLabel : t('kpi.errors_none')}
          notch={[{ icon: 'triangle-alert', label: t('kpi.open'), onClick: () => navigate(failing?.website ? paths.site(failing.website, 'overview') : paths.sites()) }]}
          onClick={() => navigate(failing?.website ? paths.site(failing.website, 'overview') : paths.sites())}
        />
        {/* P6-E02 (additive): the funnel, and for a PM their tasks, in the KPI row's free columns. */}
        <div className={s.kpiExtra}>
          <FunnelCard />
        </div>
        {!isAdmin && (
          <div className={s.kpiExtra}>
            <MyTasksCard alerts={d.alerts} />
          </div>
        )}
      </div>
      <div className={s.grid300}>
        <Card texture="dots" relative className={s.chart}>
          <Notch actions={[{ icon: 'sparkles', label: t('hero.sparkles') }]} />
          <CardHead eyebrow={t(`chart.eyebrow.${period}`)} title={t('chart.title')} />
          <div className={s.bars} role="list" aria-label={t('chart.label')}>
            {(bars.length ? bars : Array.from({ length: 8 }, () => null)).map((b, i, list) => {
              const last = i === list.length - 1;
              const v = b ? b.demo + b.production : 0;
              const label = b ? barLabel(b.label, monthsShort) : '';
              return (
                <div key={b?.label ?? i} className={s.barCol} role="listitem" aria-label={b ? tv('chart.bar', { label, count: v }) : undefined}>
                  <div className={s.track}>
                    {b && (
                      <>
                        <div className={`${s.fill} ${last ? s.fillLast : ''}`} style={{ height: `${Math.round((v / max) * 100)}%` }} />
                        <span className={`${s.bubble} ${last ? s.bubbleLast : ''}`}>{v}</span>
                      </>
                    )}
                  </div>
                  <span className={`${s.barLabel} ${last ? s.barLabelLast : ''}`}>{label}</span>
                </div>
              );
            })}
          </div>
          {!bars.length && <span className={s.chartEmpty}>{t('chart.empty')}</span>}
        </Card>
        <Card className={s.donutCard}>
          <CardHead eyebrow={t('gen.eyebrow')} title={tv('gen.title', { month: monthsPrep[month] })} className={s.stretch} />
          <div className={s.donut} role="img" aria-label={gen.used !== null && gen.limit ? t('gen.label', { used: gen.used, limit: gen.limit }) : t('gen.no_limit')}>
            <svg viewBox="0 0 200 200" className={s.donutSvg}>
              <defs>
                <pattern id={patternId} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="5" className={s.hatchLine} strokeWidth="1.5" />
                </pattern>
              </defs>
              <circle cx="100" cy="100" r="80" fill="none" stroke={`url(#${patternId})`} strokeWidth="18" />
              {genPct > 0 && <circle cx="100" cy="100" r="80" fill="none" className={s.arc} strokeWidth="18" strokeLinecap="round" pathLength={100} strokeDasharray={`${genPct} 100`} />}
            </svg>
            <div className={s.donutCenter}>
              <span className={s.donutValue}>{gen.used ?? dash}</span>
              <span className={s.donutOf}>{gen.limit ? t('gen.of', { limit: gen.limit }) : t('gen.no_limit')}</span>
            </div>
          </div>
          <div className={s.genTiles}>
            <div className={s.genTile}>
              <span className={s.genLabel}>{t('gen.spend')}</span>
              <span className={s.genValue}>{gen.ai_spend_usd === null ? dash : formatUsd(gen.ai_spend_usd)}</span>
            </div>
            <div className={s.genTile}>
              <span className={s.genLabel}>{t('gen.avg')}</span>
              <span className={s.genValue}>{gen.average_per_variant_usd === null ? dash : formatUsd(gen.average_per_variant_usd, 2)}</span>
            </div>
          </div>
        </Card>
      </div>
      <div className={s.grid320}>
        <Card padding={8}>
          <div className={s.listHead}>
            <span className={s.listTitle}>{t('jobs.title')}</span>
            <span className={s.listHint}>{t('jobs.hint')}</span>
          </div>
          {jobs.length === 0 && <div className={s.listEmpty}>{t('jobs.empty')}</div>}
          {jobs.map((j) => (
            <button key={j.id} type="button" className={s.row} onClick={() => openJob(j)}>
              <IconTile icon={JOB_ICON[j.type] ?? 'refresh-cw'} size={38} iconSize={17} />
              <div className={s.jobBody}>
                <div className={s.jobLine}>
                  <span className={s.jobName}>{tv(`jobs.name.${j.type.replace(/\./g, '_')}`, { target: jobTarget(j), defaultValue: j.type })}</span>
                  <span className={s.jobStatus}>{jobStatus(j)}</span>
                </div>
                <div className={s.jobTrack}>
                  <div className={s.jobFill} style={{ width: `${Math.round(j.progress * 100)}%` }} />
                </div>
              </div>
            </button>
          ))}
        </Card>
        <Card padding={8}>
          <div className={s.listHead}>
            <span className={s.listTitle}>{t('alerts.title')}</span>
            <span className={s.alertsCount}>{t('alerts.count', { count: d.alerts.length })}</span>
          </div>
          {d.alerts.length === 0 && <div className={s.listEmpty}>{t('alerts.empty')}</div>}
          {d.alerts.map((a, i) => {
            const target = (a.target ?? {}) as Record<string, unknown>;
            const meta = alertMeta(a.kind, target, a.severity);
            const icon = meta.icon;
            const title = alertTitle(a.kind, a.title, target, tv);
            return (
              <button key={`${a.kind}-${i}`} type="button" className={s.row} onClick={() => openAlert(target, a.kind)}>
                <IconTile icon={icon} size={38} iconSize={17} />
                <div className={s.alertBody}>
                  <span className={s.alertTitle}>{title}</span>
                  {a.subtitle && <span className={s.alertSub}>{a.subtitle}</span>}
                </div>
                <Pill tone={meta.tone}>{tv(`alerts.pill.${a.kind}`, { defaultValue: a.kind })}</Pill>
              </button>
            );
          })}
        </Card>
      </div>
    </>
  );
}

/**
 * «Воронка» (P6-E02, additive, in the KPI card's language): the share of demos that reached production and the
 * two stages' counts (`GET /admin/reports/funnel`, P6-E01), with the publish success rate of the last 30 days
 * (`GET /admin/reports/publishing`) as the pill. «—» until the API has them.
 */
function FunnelCard() {
  const { t } = useTranslation('dashboard');
  const dash = useTranslation('common').t('dash');
  const navigate = useNavigate();
  const f = demoToProduction(useQuery(funnelQuery).data);
  const rate = useQuery(publishingQuery).data?.success_rate;
  const pct = (x: number | null | undefined) => (typeof x === 'number' ? `${formatNumber(Math.round(x * 100))}%` : dash);
  return (
    <Kpi
      eyebrow={t('funnel.eyebrow')}
      title={t('funnel.title')}
      value={pct(f?.share)}
      delta={typeof rate === 'number' ? t('funnel.success', { pct: pct(rate) }) : undefined}
      sub={<span className={s.clip}>{f?.demo ? t('funnel.sub', { demo: formatNumber(f.demo), production: formatNumber(f.production) }) : t('funnel.empty')}</span>}
      notch={[{ icon: 'percent', label: t('kpi.open'), onClick: () => navigate(paths.sites()) }]}
      onClick={() => navigate(paths.sites())}
    />
  );
}
